import { px2em } from '../../../px2em/@impl/px2em.js';
import warning from 'fbjs/lib/warning';

/**
 * Forming selector for poll, quiz options in hover state
 * @param {string} selector
 * @return {string[]}
 */
export const hoverState = (selector) => [
  `.web ${selector}[data-hover]`,
  `.web ${selector}:hover`,
  `.tv ${selector}[data-focus]`,
  `.tv ${selector}:focus`,
];

/**
 *
 * @param {string} parentSelector
 * @param {string} selector
 * @return {string[]}
 */
export const parentHoverState = (parentSelector, selector) =>
  hoverState(parentSelector).map(
    (parentSelector) => `${parentSelector} ${selector}`,
  );

/**
 * Forming selector for parent of poll, quiz options in focus state for tv
 * @param {string} selector
 * @return {string[]}
 */
export const focusState = (selector) => [
  `.tv ${selector}[data-focus]`,
  `.tv ${selector}:focus`,
];

/**
 *
 * @param parentSelector
 * @param {string} selector
 * @return {string[]}
 */
export const parentFocusState = (parentSelector, selector) =>
  focusState(parentSelector).map(
    (parentSelector) => `${parentSelector} ${selector}`,
  );

/**
 *
 * @param {string} selector
 * @return {string}
 */
export const outliner = (selector) =>
  `${selector} > [role="outline-decorator"]`;

/**
 *
 * @param {string} selector
 * @return {string[]}
 */
export const activeState = (selector) => [
  `.web ${selector}[data-active]`,
  `.web ${selector}:active`,
  `.mobile ${selector}[data-active]`,
  `.mobile ${selector}:active`,
];

/**
 *
 * @param {string} selector
 * @return {string[]}
 */
export const mobileActiveState = (selector) => [
  `.mobile ${selector}[data-active]`,
  `.mobile ${selector}:active`,
];

/**
 *
 * @param {string} selector
 * @return {string[]}
 */
export const disabledState = (selector) => [
  `.web ${selector}[data-disabled=true]`,
  `.web ${selector}:disabled`,
  `.mobile ${selector}[data-disabled=true]`,
  `.mobile ${selector}:disabled`,
  `.tv ${selector}[data-disabled=true]`,
  `.tv ${selector}:disabled`,
];

/**
 *
 * @param {string} selector
 * @return {string[]}
 */
export const disabledFocusState = (selector) => [
  `.tv ${selector}[data-disabled="true"][data-focus]`,
  `.tv ${selector}[data-disabled="true"]:focus`,
];

/**
 * Forming selector for parent of poll, quiz options in active state parent
 * @param parentSelector
 * @param {string} selector
 * @return {string[]}
 */
export const parentActiveState = (parentSelector, selector) =>
  activeState(parentSelector).map(
    (parentSelector) => `${parentSelector} ${selector}`,
  );

/**
 * Forming selector for poll, quiz options in selected state
 * @param {string} selector
 * @return {string}
 */
export const selectedState = (selector) => `${selector}[data-voted="true"]`;

/**
 * Forming selector for poll, quiz options in correct answer state
 * @param {string} selector
 * @return {string}
 */
export const correctState = (selector) => `${selector}[data-correct="true"]`;
export const incorrectSelectedState = (selector) =>
  `${selector}[data-voted="true"]`;
export const correctSelectedState = (selector) =>
  `${selector}[data-voted="true"][data-correct="true"]`;

export const parentSelectedState = (parentSelector, selector) =>
  `${parentSelector}[data-voted="true"] ${selector}`;

/**
 *
 * @param {object} widget
 * @param {string} widget.id
 * @return {string}
 */
export const widgetSelector = (widget) => `#${widget.id}`;

export const relativeBorderRadius = (borderRadius, { width, height }) => {
  return `${100 * (borderRadius / width)}% / ${100 * (borderRadius / height)}%`;
};

const asIs = (val) => val;
const transform = (type) => (value) => `${type}(${value})`;

const mapProperties = new Map([
  [
    'scale',
    {
      name: 'transform',
      value: transform('scale'),
    },
  ],
  [
    'opacity',
    {
      name: 'opacity',
      value: asIs,
    },
  ],
  [
    'borderRadius',
    {
      name: 'border-radius',
      value: relativeBorderRadius,
    },
  ],
]);

export function getOutlineDeclaration(outlineDescription) {
  const {
    borderColor: color,
    borderSize: width,
    borderGap: offset = 0,
  } = outlineDescription;
  if (
    [color, width, offset].some((value) => {
      if (typeof value === 'undefined') {
        return true;
      }
    })
  ) {
    console.log('does not have all outline properties');
    return;
  }
  return [
    ['bottom', `-${px2em(offset)}`],
    ['left', `-${px2em(offset)}`],
    ['right', `-${px2em(offset)}`],
    ['top', `-${px2em(offset)}`],
    ['content', '""'],
    ['border-radius', 'inherit'],
    ['position', 'absolute'],
    ['box-shadow', `0 0 0 ${px2em(width)} ${color}`],
  ];
}

export function getStatesDeclaration(stateDescription, ctx) {
  if (!stateDescription) {
    return [];
  }

  return Object.keys(stateDescription)
    .map((key) => {
      if (mapProperties.has(key)) {
        const translator = mapProperties.get(key);
        return [translator.name, translator.value(stateDescription[key], ctx)];
      }

      warning(true, `Unknown property ${key}`);
    })
    .filter(Boolean);
}

export function justifyContentValueReplace(hAlign) {
  switch (hAlign) {
    case 'left':
      return 'flex-start';
    case 'right':
      return 'flex-end';
    default:
      return hAlign;
  }
}
